import { CrazyGames } from './crazygames';
import { GameMonetize } from './gamemonetize';

/**
 * Provider-agnostic ad + portal-signal facade. The active provider is fixed at build time via the
 * `__AD_PROVIDER__` define (see vite.config.ts):
 *   'crazygames'   → CrazyGames portal build
 *   'gamemonetize' → our own hosting + itch.io build
 *   'none'         → local dev / a build with no ad SDK
 *
 * Gameplay signals (gameplayStart/Stop, happytime, loadingStop) are CrazyGames-specific; GameMonetize
 * has no equivalent, so they no-op there. The CrazyGames Data Module (save sync) is unrelated to ads
 * and stays on CrazyGames.* directly.
 */
declare const __AD_PROVIDER__: string;
type Provider = 'crazygames' | 'gamemonetize' | 'none';
const PROVIDER = (__AD_PROVIDER__ as Provider) || 'none';

export const Ads = {
  provider: PROVIDER,

  async init(): Promise<void> {
    if (PROVIDER === 'crazygames') return CrazyGames.init();
    if (PROVIDER === 'gamemonetize') return GameMonetize.init();
  },

  /** Game finished loading and is interactive (CrazyGames signal; no-op elsewhere). */
  loadingStop(): void { if (PROVIDER === 'crazygames') CrazyGames.loadingStop(); },

  /** Wrap ACTUAL gameplay so the portal doesn't show ads during a race (CrazyGames signal). */
  gameplayStart(): void { if (PROVIDER === 'crazygames') CrazyGames.gameplayStart(); },
  gameplayStop(): void { if (PROVIDER === 'crazygames') CrazyGames.gameplayStop(); },
  happytime(): void { if (PROVIDER === 'crazygames') CrazyGames.happytime(); },

  /** Rewarded ad: `onReward` runs only when the reward is earned; `onClose` always runs at the end. */
  rewarded(onReward: () => void, onClose?: () => void): void {
    if (PROVIDER === 'crazygames') return CrazyGames.rewarded(onReward, onClose);
    if (PROVIDER === 'gamemonetize') return GameMonetize.rewarded(onReward, onClose);
    // No provider (dev / pre-approval): grant the reward so the feature still works.
    onReward(); onClose?.();
  },

  /** Interstitial ad at a natural break. `onDone` always runs at the end. */
  interstitial(onDone?: () => void): void {
    if (PROVIDER === 'crazygames') return CrazyGames.midgame(onDone);
    if (PROVIDER === 'gamemonetize') return GameMonetize.interstitial(onDone);
    onDone?.();
  },
};
